/**
 * API URL constants resolved per environment (production, development, local, preview).
 *
 * Exports {@link ApiBaseUrls} for service base URLs, {@link API_ENDPOINTS} for
 * fully-qualified endpoint paths, and {@link RPA_PATHS_ONLY_FOR_QUEUE} for
 * RPA task queue relative paths.
 * @module apiUrls
 */

import {
  resolveAuthUserUrl,
  resolveGatewayService,
  resolveRpaGateway,
} from "../../utils/gatewayEnv";

const RPA_SERVICE_CLOUDRUN = `${resolveRpaGateway()}/rpa-service/v1/`;

const EV_BV_SERVICE = resolveGatewayService(
  "coverage-service",
  "https://apis.risalabs.ai/backend-service/medical/",
  "https://apis-dev.risalabs.ai/coverage-service/",
);

const PA_ORDER_CREATION_SERVICE = resolveGatewayService(
  "pa-order-creation",
  "https://apis.risalabs.ai/pa-order-creation/",
  "https://apis-dev.risalabs.ai/pa-order-creation/",
);

/** Base URL for pharmacy-pa-order-creation (distinct from medical ``pa-order-creation``). */
const PHARMACY_PA_ORDER_CREATION_SERVICE = resolveGatewayService(
  "pharmacy-pa-order-creation",
  "https://apis.risalabs.ai/pharmacy-pa-order-creation/",
  "https://apis-dev.risalabs.ai/pharmacy-pa-order-creation/",
);

const PHARMACY_RPA_SERVICE = resolveGatewayService(
  "pharmacy-rpa-service",
  "https://apis.risalabs.ai/pharmacy-rpa-service/",
  "https://apis-dev.risalabs.ai/pharmacy-rpa-service/",
);

const BACKEND_SERVICE = resolveGatewayService(
  "backend-service",
  "https://apis.risalabs.ai/backend-service/",
  "https://apis-dev.risalabs.ai/backend-service/",
);

const BASE_URL = resolveGatewayService(
  "",
  "https://apis.risalabs.ai/",
  "https://apis-dev.risalabs.ai/",
);

const AI_SERVICE = resolveGatewayService(
  "ai-service",
  "https://apis.risalabs.ai/ai-service/",
  "https://apis-dev.risalabs.ai/ai-service/",
);

const AUTH_URL = resolveAuthUserUrl();

const PA_ORDER_LEGACY_SUBMISSION_SERVICE_URL = `${resolveRpaGateway()}/pa-order-creation/`;

const RPA_SERVICE_ONE_RISA = resolveGatewayService(
  "rpa-service",
  "https://apis.risalabs.ai/rpa-service/",
  "https://apis-dev.risalabs.ai/rpa-service/",
);

/** Base URLs for each backend service, resolved per environment. */
export const ApiBaseUrls = {
  AI_SERVICE: AI_SERVICE,
  AUTH_URL: AUTH_URL,
  EV_BV_SERVICE: EV_BV_SERVICE,
  LOCAL_HOST: "http://127.0.0.1:8000/rpa-service/",
  RPA_SERVICE_CLOUDRUN: RPA_SERVICE_CLOUDRUN,
  PA_ORDER_CREATION_SERVICE: PA_ORDER_CREATION_SERVICE,
  PA_ORDER_LEGACY_SUBMISSION_SERVICE: PA_ORDER_LEGACY_SUBMISSION_SERVICE_URL,
  PHARMACY_PA_ORDER_CREATION_SERVICE: PHARMACY_PA_ORDER_CREATION_SERVICE,
  BACKEND_SERVICE: BACKEND_SERVICE,
  BASE_URL: BASE_URL,
  DEV_SUBMI_PA_FORM:
    "https://apis.risalabs.ai/prior-auth-submission/api/v1/pa-submission/submit-pa-form",
  PHARMACY_RPA_SERVICE: PHARMACY_RPA_SERVICE,
  PROD_SUBMI_PA_FORM:
    "https://apis.risalabs.ai/prior-auth-submission/api/v1/pa-submission/submit-pa-form",
  PROXY_BASE_URL:
    "https://us-central1-prior--backen-prod-svc-u4g8.cloudfunctions.net/http-proxy",
  RPA_SERVICE_ONE_RISA: RPA_SERVICE_ONE_RISA,
} as const;

/** Fully-qualified API endpoint URLs used throughout the dashboard. */
export const API_ENDPOINTS = {
  ADD_EDIT_GI_CONFIGURATION: `${ApiBaseUrls.RPA_SERVICE_ONE_RISA}medical/group-inbox/push-config`,
  ADD_EDIT_PORTAL_CREDENTIAL: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}commons/portal/add-credential`,
  ALGOLIA_FACET_COUNTS: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/utility/algolia-facet-counts`,
  ALGOLIA_MULTI_FACET_COUNTS: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/utility/algolia-multi-facet-counts`,
  ALGOLIA_SEARCH: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/utility/algolia-search`,
  APPEND_ROW_TO_GSHEET: `${ApiBaseUrls.BACKEND_SERVICE}commons/gsheet/append-data-to-specified-sheet`,
  AUDIT_TRIAL_OVERVIEW: `${ApiBaseUrls.BACKEND_SERVICE}commons/audit-trail/get-audit-trail-overview`,
  AUTH_LETTER_REVIEW_DUMP: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-on-file/submit-auth-letter-entry`,
  AUTH_ON_FILE_CHECK: `${ApiBaseUrls.RPA_SERVICE_ONE_RISA}medical/auth-check`,
  AUTH_STATUS_CHANGE_COMMENTS: `${ApiBaseUrls.BACKEND_SERVICE}medical/frontend-query/insert/bulk/auth-status-comments`,
  GET_AUTH_LETTER_AGENT_DIFF: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-on-file/get-diff-from-agent`,
  UPDATE_ORDER_REVIEW_STATUS: `${ApiBaseUrls.BACKEND_SERVICE}medical/prior-auth-utilities/order-review-status`,
  AUTH_TOKEN: `${ApiBaseUrls.BACKEND_SERVICE}auth/token`,
  BEARER_TOKEN_URL: `${ApiBaseUrls.AUTH_URL}/token`,
  CHECK_AVAILITY_PAYER_ID: `${ApiBaseUrls.BACKEND_SERVICE}commons/availity/check-availity-payer-id`,
  CHECK_CLAIMS_FOR_CSV: `${ApiBaseUrls.BACKEND_SERVICE}commons/claims/check-claims-for-csv`,
  CHECK_RATE_LIMIT_PASSWORD_RESET: `${ApiBaseUrls.AUTH_URL}/check-rate-limit-password-reset`,
  COMMENT_RPA_MODAL_SAVE: `${ApiBaseUrls.BACKEND_SERVICE}medical/prior-auth-utilities/mark-as-completed`,
  COPY_DOCUMENTS_BY_PATH: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/utility/copy-documents-by-path`,
  CREATE_PORTAL_SECRET: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}commons/portal/create-secret`,
  DELETE_CMM_ORDER: `${ApiBaseUrls.RPA_SERVICE_CLOUDRUN}delete-entry-on-cmm`,
  DETERMINE_CLINICAL_QUESTIONNAIRE_LLM: `${ApiBaseUrls.BASE_URL}ai-service/commons/medical-necessity/get-medical-necessity-llm`,
  DETERMINE_ICD_CODE: `${ApiBaseUrls.BASE_URL}medical-necessity/v1/nycbs/determine-icd-code-llm`,
  DETERMINE_ICD_CODE_LLM: `${ApiBaseUrls.BASE_URL}ai-service/commons/icd-code-detect/determine-icd-code-llm`,
  DOWNLOAD_AUDIT_TRAIL: `${ApiBaseUrls.RPA_SERVICE_CLOUDRUN}utility_apis/download-audit-trail`,
  EDIT_COVER_MY_MEDS_REQUEST: `${ApiBaseUrls.RPA_SERVICE_CLOUDRUN}edit-cover-my-meds_request`,
  EV_BV_FROM_EXCEL: `${ApiBaseUrls.EV_BV_SERVICE}coverage/check-coverage-for-csv`,
  FETCH_AUTH_GRID: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-grid/show-auth-grid`,
  FETCH_NCCN_GUIDELINES: `${ApiBaseUrls.AI_SERVICE}medical/guidelines/fetch-guidelines`,
  FETCH_PORTAL_PASSWORD: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}commons/portal/fetch-password`,
  GET_ALL_DIAGNOSIS_CODES_FOR_ORDER: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}get-diagnosis-details`,
  GET_ALL_DOCUMENTS_FOR_ORDER: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}documents`,
  GET_ALL_INSURANCE_DATA_FOR_ORDER: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}get-insurance-details`,
  GET_ALL_QUESTIONS_FOR_ORDER: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}questionnaire-data`,
  GET_ASSIGNED_ORDERS: `${ApiBaseUrls.RPA_SERVICE_CLOUDRUN}nycbs_bg_query/get-assigned-cmm-orders-with-status`,
  GET_AUDIT_TRIAL_DETAILS: `${ApiBaseUrls.BACKEND_SERVICE}commons/audit-trail/get-detailed-audit-trail`,
  GET_AUTH_GRID_SOURCE: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-grid/get-auth-grid-source`,
  GET_DX_CODE_MAP_NCCN: `${ApiBaseUrls.EV_BV_SERVICE}icdcode/get-dxcodemap-nccn`,
  GET_EV_BV_REPORT_DETAILS: `${ApiBaseUrls.EV_BV_SERVICE}coverage/prepare-coverage-excl`,
  GET_FACETS_COUNT_FROM_POSTGRES: `${ApiBaseUrls.BACKEND_SERVICE}medical/frontend-query/facet-counts`,
  GET_FAX_STATUS: `${ApiBaseUrls.BACKEND_SERVICE}commons/fax/get-fax-status`,
  GET_FILE_FROM_NYCBS_SFTP: `${ApiBaseUrls.PHARMACY_PA_ORDER_CREATION_SERVICE}nycbs/get-file-from-nycbs-sftp`,
  GET_FILENAME_SUMMARY: `${ApiBaseUrls.BACKEND_SERVICE}commons/bigquery/filename-summary`,
  LIST_FIRESTORE_COLLECTIONS: `${ApiBaseUrls.BACKEND_SERVICE}commons/firestore/collections`,
  GET_FINAL_WORKLIST_DATA: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/external-worklist/get-data`,
  GET_FORM_NAME: `${ApiBaseUrls.BACKEND_SERVICE}medical/priority-health/get-form-name`,
  /** Latest Group Inbox configurations for every eligible view tab. */
  GROUP_INBOX_CONFIGS: `${ApiBaseUrls.BACKEND_SERVICE}commons/configs/group-inbox`,
  GET_INTERNAL_PROCESSED_ORDER_STATUS: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}get-processed-pa-orders`,
  GET_MEDICAL_PA_ANALYTICS: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/external-worklist/get-analytics`,
  GET_PATIENT_ELIGIBILITY_DETAILS: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}get-patient-eligibility-details`,
  GET_PHARMA_STP_FILE_DATA: `${ApiBaseUrls.BACKEND_SERVICE}commons/bigquery/get-data-from-bigquery`,
  GET_PRESCRIPTION_DATA: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}prescription-data`,
  GET_SINGLE_ORDER: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}get-single-order-details`,
  GET_UNASSIGNED_ORDERS: `${ApiBaseUrls.RPA_SERVICE_CLOUDRUN}nycbs_bg_query/get-unassigned-cmm-orders`,
  GET_UNIQUE_CMM_ORDERS: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}get-unique-cmm-orders`,
  INGEST_PDF_CSV_NAR_GRID: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-grid/ingest-pdf-csv-nar-grid`,
  KILL_SWITCH: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-grid/kill-switch`,
  LIST_HISTORIC_NAR: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-grid/list-historic-nar-recommendations`,
  INGEST_HISTORIC_NAR: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-grid/ingest-historic-nar-recommendation`,
  CLEAR_HISTORIC_NAR_LANE: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-grid/clear-nar-lane`,
  LOG_EVENT_ENDPOINT: `${ApiBaseUrls.BACKEND_SERVICE}commons/audit-trail/log-frontend-event`,
  MEDICAL_PA_SUBMISSION: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/submission-medical-pa/submit-medical-pa-order`,
  MEMBER_BASE: `${ApiBaseUrls.BACKEND_SERVICE}commons/rbac/members`,
  MEMBER_BULK_UPDATE: `${ApiBaseUrls.BACKEND_SERVICE}commons/rbac/members/bulk-update`,
  MEMBER_INVITE: `${ApiBaseUrls.BACKEND_SERVICE}commons/rbac/members/invite`,
  MERGE_PDFS: `${ApiBaseUrls.BACKEND_SERVICE}commons/pdf-operations/merge-pdfs`,
  NAR_RPA_TRIGGER: `${ApiBaseUrls.RPA_SERVICE_ONE_RISA}medical/nar/availity-healthfirst-checknar`,
  ONCOEMR_TASKS: `${ApiBaseUrls.BACKEND_SERVICE}oncoemr-tasks`,
  ONCOEMR_TASKS_USERS_SEARCH: `${ApiBaseUrls.BACKEND_SERVICE}oncoemr-tasks/users/search`,
  ORDERS_BULK_UPDATE: `${ApiBaseUrls.BACKEND_SERVICE}medical/frontend-query/update/bulk/order`,
  POST_STATUS_TRACKING: `${ApiBaseUrls.BASE_URL}ai-service/pharmacy/astera/push-orders`,
  PREPARE_CLAIMS_EXCEL: `${ApiBaseUrls.BACKEND_SERVICE}commons/claims/prepare-claims-excl`,
  PREPARE_FORM_QUESTIONNAIRE: `${ApiBaseUrls.BACKEND_SERVICE}commons/fax/prepare-form-questionnaire`,
  PROCESS_CSV_PHARMACY_PA: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/external-worklist/process-csv`,
  PUSH_FILES_TO_SFTP: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/nycbs/push-files-to-sftp`,
  RE_RUN_ONCO_EMR_WORKFLOW: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}submit-pa`,
  REFETCH_CLINICAL_ATTACHMENT: `${ApiBaseUrls.RPA_SERVICE_ONE_RISA}commons/clinical-docs`,
  REFRESH_MEDICAL_PA_ORDERS: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/core-medical-pa/start-medical-pa`,
  REFRESH_TEMPLATES: `${ApiBaseUrls.BACKEND_SERVICE}medical/prior-auth-utilities/refresh-templates`,
  RPA_TASK_QUEUE: `${ApiBaseUrls.BACKEND_SERVICE}commons/queue/rpa`,
  NEW_CENTURY_HEALTH_QUESTIONNAIRE: `${ApiBaseUrls.RPA_SERVICE_ONE_RISA}medical/pa-submission/new-century-health/questionnaire/fetch`,
  NEW_CENTURY_HEALTH_QUESTIONNAIRE_UPDATE: `${ApiBaseUrls.RPA_SERVICE_ONE_RISA}medical/pa-submission/new-century-health/questionnaire`,
  LEGACY_MEDICAL_PA_SUBMISSION_URL: `${ApiBaseUrls.PA_ORDER_LEGACY_SUBMISSION_SERVICE}v1/medical-pa/submit-medical-pa-order`,
  RUN_STATUS_TRACKING: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}run_status_tracking`,
  SEARCH_FOR_MEDICAL_PA_ORDERS_FROM_POSTGRES: `${ApiBaseUrls.BACKEND_SERVICE}medical/frontend-query/search`,
  SEARCH_IN_CMM_INPUT_TABLE: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}search-in-cmm-table`,
  SEND_FAX_FROM_CONFIG: `${ApiBaseUrls.BACKEND_SERVICE}commons/fax/send-fax-from-config`,
  SEND_INVITE_VIA_EMAIL: `${ApiBaseUrls.AUTH_URL}/invite-user-to-dashboard`,
  SEND_TO_PLAN_IN_CMM: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}send-to-plan`,
  SINGLE_ORDER_DETAILS_GENERIC_CALL_FOR_POSTGRES: `${ApiBaseUrls.BACKEND_SERVICE}medical/frontend-query`,
  SINGLE_ORDER_DETAILS_GENERIC_CALL_FOR_POSTGRES_INSERT: `${ApiBaseUrls.BACKEND_SERVICE}medical/frontend-query/insert`,
  SINGLE_ORDER_DETAILS_GENERIC_CALL_FOR_POSTGRES_UPDATE: `${ApiBaseUrls.BACKEND_SERVICE}medical/frontend-query/update`,
  SUBMIT_FAX_FORM_QUESTIONNAIRE: `${ApiBaseUrls.BACKEND_SERVICE}commons/fax/submit`,
  TEMPLATE_VALIDATOR_VALIDATE: `${ApiBaseUrls.AI_SERVICE}medical/template-validator/validate-batch`,
  UPDATE_BIGQUERY_DATA: `${ApiBaseUrls.BACKEND_SERVICE}commons/bigquery/update-data-to-bigquery`,
  UPDATE_CIG_BOX_DATA: `${ApiBaseUrls.RPA_SERVICE_ONE_RISA}medical/auth-check/write-back-oncoemr`,
  UPDATE_CMM_TABLE: `${ApiBaseUrls.RPA_SERVICE_CLOUDRUN}nycbs_bg_query/update-cmm-table`,
  UPDATE_J_CODE_DOCUMENTS: `${ApiBaseUrls.BACKEND_SERVICE}medical/prior-auth-utilities/update-j-code-documents`,
  UPDATE_J_CODES: `${ApiBaseUrls.BACKEND_SERVICE}medical/prior-auth-utilities/update-j-codes`,
  UPDATE_PROFILE: `${ApiBaseUrls.BACKEND_SERVICE}commons/profile/update`,
  UPDATE_QUESTIONAIRE_RESPONSE: `${ApiBaseUrls.RPA_SERVICE_CLOUDRUN}edit-cover-my-meds-questionnaire`,
  UPDATE_REPORTS: `${ApiBaseUrls.BACKEND_SERVICE}medical/prior-auth-utilities/update-reports`,
  UPLOAD_AUTH_LETTER: `${ApiBaseUrls.RPA_SERVICE_ONE_RISA}commons/upload-authorization-letter`,
  VIEW_ALL_AUDIT_TRAIL: `${ApiBaseUrls.BACKEND_SERVICE}commons/audit-trail/get-audit-trail`,
  CANDID_FILLING_RPA_TRIGGER: `${ApiBaseUrls.RPA_SERVICE_ONE_RISA}medical/candid-filling`,
  DELETE_J_CODE: `${ApiBaseUrls.BACKEND_SERVICE}medical/prior-auth-utilities/delete-j-code`,
  ADD_J_CODE: `${ApiBaseUrls.BACKEND_SERVICE}medical/prior-auth-utilities/add-j-code`,
};

/** Relative RPA path segments used exclusively for the RPA task queue. */
export const RPA_PATHS_ONLY_FOR_QUEUE = {
  AUTH_ON_FILE_CHECK: `medical/auth-check/`,
  NAR_RPA_TRIGGER: `medical/nar/availity-healthfirst-checknar`,
  UPLOAD_AUTH_LETTER: `commons/upload-authorization-letter`,
  WRITE_BACK_COMMENTS: `medical/auth-check/write-back-oncoemr`,
  CLINICAL_DOCS: `commons/clinical-docs`,
} as const;
